import type { ColorSwatch, ProductSwatchEntry, ProductSwatchesConfig, ProductSwatchesRegistryData } from '../types';

const CACHE_KEY = 'td-product-swatches-cache-v1';
const ONE_HOUR_MS = 60 * 60 * 1000;
const MAX_PRODUCT_PAGES = 20;
const PAGE_SIZE = 250;

const EMPTY_DATA: ProductSwatchesRegistryData = { colors: {}, products: {} };

type CachePayload = ProductSwatchesRegistryData & { fetchedAt: number };

type MetaobjectField = { value: string | null } | null;

type ColorMetaobjectNode = {
  handle: string;
  label: MetaobjectField;
  color: MetaobjectField;
};

type ProductNode = {
  id: string;
  handle: string;
  articleNumber: MetaobjectField;
  options: { name: string; values: string[] }[];
};

type PageInfo = { hasNextPage: boolean; endCursor: string | null };

let readyPromise: Promise<ProductSwatchesRegistryData> | null = null;

function handleize(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toNumericId(gid: string): string {
  const match = gid.match(/(\d+)$/);
  return match ? match[1] : gid;
}

function getConfig(): ProductSwatchesConfig | null {
  const configEl = document.querySelector('[data-ref="product-swatches-config"]');
  if (!configEl?.textContent) return null;

  try {
    const parsed = JSON.parse(configEl.textContent) as ProductSwatchesConfig;
    if (!parsed.storefrontAccessToken || !parsed.apiVersion) return null;
    return parsed;
  } catch {
    return null;
  }
}

function readCache(): ProductSwatchesRegistryData | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CachePayload;
    if (Date.now() - parsed.fetchedAt > ONE_HOUR_MS) return null;

    return { colors: parsed.colors, products: parsed.products };
  } catch {
    return null;
  }
}

function writeCache(data: ProductSwatchesRegistryData): void {
  try {
    const payload: CachePayload = { ...data, fetchedAt: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Storage full or unavailable (e.g. private browsing) - fail silently, in-memory data is still usable.
  }
}

async function graphqlRequest<TData>(
  config: ProductSwatchesConfig,
  query: string,
  variables: Record<string, unknown>,
): Promise<TData> {
  const endpoint = `${window.location.origin}/api/${config.apiVersion}/graphql.json`;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': config.storefrontAccessToken,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!response.ok) {
    throw new Error(`Storefront API request failed (${response.status})`);
  }

  const json = (await response.json()) as { data?: TData; errors?: unknown[] };

  if (json.errors?.length) {
    throw new Error(`Storefront API returned errors: ${JSON.stringify(json.errors)}`);
  }

  if (!json.data) {
    throw new Error('Storefront API returned no data');
  }

  return json.data;
}

const COLORS_QUERY = `
  query ColorMetaobjects($first: Int!, $cursor: String) {
    metaobjects(type: "color", first: $first, after: $cursor) {
      edges {
        node {
          handle
          label: field(key: "label") { value }
          color: field(key: "color") { value }
        }
      }
      pageInfo { hasNextPage endCursor }
    }
  }
`;

const PRODUCTS_QUERY = `
  query ProductsForSiblings($first: Int!, $cursor: String) {
    products(first: $first, after: $cursor) {
      edges {
        node {
          id
          handle
          articleNumber: metafield(namespace: "itp-pim", key: "articleNumber") { value }
          options(first: 3) { name values }
        }
      }
      pageInfo { hasNextPage endCursor }
    }
  }
`;

async function fetchAllColors(config: ProductSwatchesConfig): Promise<Record<string, ColorSwatch>> {
  const colors: Record<string, ColorSwatch> = {};
  let cursor: string | null = null;

  for (let page = 0; page < MAX_PRODUCT_PAGES; page += 1) {
    const data: { metaobjects: { edges: { node: ColorMetaobjectNode }[]; pageInfo: PageInfo } } = await graphqlRequest(
      config,
      COLORS_QUERY,
      { first: PAGE_SIZE, cursor },
    );

    for (const { node } of data.metaobjects.edges) {
      const hex = node.color?.value;
      const label = node.label?.value;
      if (!hex || !label) continue;

      colors[node.handle] = { handle: node.handle, label, hex };
    }

    if (!data.metaobjects.pageInfo.hasNextPage) break;
    cursor = data.metaobjects.pageInfo.endCursor;
  }

  return colors;
}

function resolveProductColorHandle(options: { name: string; values: string[] }[]): string {
  const colorOption = options.find((option) => {
    const name = option.name.toLowerCase();
    return name === 'color' || name === 'colour';
  });

  const colorValue = colorOption?.values[0];
  return colorValue ? handleize(colorValue) : '';
}

async function fetchAllProducts(
  config: ProductSwatchesConfig,
): Promise<{ id: string; handle: string; color: string; articleNumber: string }[]> {
  const products: { id: string; handle: string; color: string; articleNumber: string }[] = [];
  let cursor: string | null = null;

  for (let page = 0; page < MAX_PRODUCT_PAGES; page += 1) {
    const data: { products: { edges: { node: ProductNode }[]; pageInfo: PageInfo } } = await graphqlRequest(
      config,
      PRODUCTS_QUERY,
      { first: PAGE_SIZE, cursor },
    );

    for (const { node } of data.products.edges) {
      const articleNumber = node.articleNumber?.value;
      if (!articleNumber) continue;

      products.push({
        id: toNumericId(node.id),
        handle: node.handle,
        color: resolveProductColorHandle(node.options),
        articleNumber,
      });
    }

    if (!data.products.pageInfo.hasNextPage) break;
    cursor = data.products.pageInfo.endCursor;
  }

  return products;
}

function buildProductRegistry(
  products: { id: string; handle: string; color: string; articleNumber: string }[],
): Record<string, ProductSwatchEntry> {
  const buckets = new Map<string, string[]>();

  for (const product of products) {
    const key = product.articleNumber.toLowerCase().trim();
    const bucket = buckets.get(key) ?? [];
    bucket.push(product.id);
    buckets.set(key, bucket);
  }

  const registry: Record<string, ProductSwatchEntry> = {};

  for (const product of products) {
    const key = product.articleNumber.toLowerCase().trim();
    const bucket = buckets.get(key) ?? [];

    registry[product.id] = {
      id: product.id,
      handle: product.handle,
      color: product.color,
      siblingIds: bucket.filter((id) => id !== product.id),
    };
  }

  return registry;
}

async function fetchRegistryData(config: ProductSwatchesConfig): Promise<ProductSwatchesRegistryData> {
  const [colors, products] = await Promise.all([fetchAllColors(config), fetchAllProducts(config)]);

  return { colors, products: buildProductRegistry(products) };
}

export function whenReady(): Promise<ProductSwatchesRegistryData> {
  if (readyPromise) return readyPromise;

  readyPromise = (async () => {
    const cached = readCache();
    const colorsCount = Object.keys(cached?.colors ?? {}).length;
    const productsCount = Object.keys(cached?.products ?? {}).length;

    if (cached) return cached;

    const config = getConfig();
    if (!config) {
      console.warn('[product-swatches-registry] Missing Storefront API config, skipping swatch sync.');
      return EMPTY_DATA;
    }

    try {
      const data = await fetchRegistryData(config);

      writeCache(data);
      return data;
    } catch (error) {
      console.warn('[product-swatches-registry] Failed to fetch swatch registry:', error);
      return EMPTY_DATA;
    }
  })();

  return readyPromise;
}
